import React, { useEffect, useState } from 'react';
import axios from 'axios';
import UserForm from './UserForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { useStaffAuth } from '../../../shared/StaffAuthContext';
import { ActiveStatusPill, Pill } from '../../../shared/Pill';
import { formatDateTime } from '../../../shared/tenantClock';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';

// Each staff member's sign-in state (issue #251, backend userController.js
// withLoginStatus).
const LOGIN_STATUS = {
  active: { label: 'Can sign in', color: '#1b5e20', background: '#e3f1e4' },
  paused: { label: 'Paused', color: '#8a5a00', background: '#fff3d6', title: 'Too many wrong passwords. Clears itself 15 minutes after the last attempt.' },
  invited: { label: 'Invited', color: '#1565c0', background: '#e3eefb', title: 'Invite sent. The link works once and expires 24 hours after it was sent.' },
  invite_expired: { label: 'Invite expired', color: '#b91c1c', background: '#fef2f2', title: 'The invite link expired before a password was set. Re-send it from the row menu.' },
  no_login: { label: 'No login', color: '#5a6b85', background: '#eef1f5', title: 'Not invited yet. Use Invite from the row menu.' },
};

function LoginStatusPill({ status }) {
  const meta = LOGIN_STATUS[status] || LOGIN_STATUS.no_login;
  return <Pill color={meta.color} background={meta.background} title={meta.title}>{meta.label}</Pill>;
}

// The staff member's own mailbox (issue #259), connected from their My
// Account page: whether it's connected, its last sync, the folders it syncs
// (read-only, issue #262) and any sync error. Never their password or
// never-log list.
function MailboxCell({ mailbox }) {
  if (!mailbox) return <span style={{ color: '#888' }}>—</span>;
  const lastSync = mailbox.last_synced_at ? formatDateTime(mailbox.last_synced_at) : 'Not yet';
  return (
    <div title={mailbox.email}>
      {mailbox.last_sync_error
        ? <Pill color="#b91c1c" background="#fef2f2" title={mailbox.last_sync_error}>Sync error</Pill>
        : <Pill color="#1b5e20" background="#e3f1e4" title={mailbox.email}>Connected</Pill>}
      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Last sync: {lastSync}</div>
      {mailbox.folders_summary && (
        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }} title="Folders this mailbox syncs. Only the staff member can change them, on their My Account page.">
          Folders: {mailbox.folders_summary}
        </div>
      )}
      {mailbox.last_sync_error && (
        <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={mailbox.last_sync_error}>
          {mailbox.last_sync_error}
        </div>
      )}
    </div>
  );
}

function UserTable() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const { currentUser } = useStaffAuth();
  const [notice, setNotice] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/roles')
      .then((res) => setRoles(res.data.data || []))
      .catch(() => setRoles([]));
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, page, limit]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/users', { params: { status, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Switch off ${item.name}'s login? They won't be able to sign in, and are signed out straight away if they're signed in now. Switching it back on restores access with the same password.`, { confirmLabel: 'Switch Off' }))) return;
    try {
      await axios.patch(`/api/users/${item.id}/deactivate`);
      fetchRows();
      setNotice('');
      dismissError();
    } catch (err) {
      showError(err, 'Switch off failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/users/${item.id}/reactivate`);
      fetchRows();
      setNotice('');
      dismissError();
    } catch (err) {
      showError(err, 'Switch on failed');
    }
  };

  const handleInvite = async (item) => {
    try {
      const res = await axios.post(`/api/users/${item.id}/invite`);
      setNotice(res.data.message);
      fetchRows();
      dismissError();
    } catch (err) {
      setNotice('');
      showError(err, 'Invite failed');
    }
  };

  const handleDisconnectMailbox = async (item) => {
    if (!(await askConfirm(`Disconnect ${item.name}'s mailbox (${item.mailbox.email})? Syncing it stops. Emails already logged from it stay on their records.`, { confirmLabel: 'Disconnect' }))) return;
    try {
      const res = await axios.delete(`/api/users/${item.id}/mailbox`);
      setNotice(res.data.message);
      fetchRows();
      dismissError();
    } catch (err) {
      setNotice('');
      showError(err, 'Disconnect failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/users/${editItem.id}`, data);
      } else {
        await axios.post('/api/users', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Users</h2>
        <button
          onClick={handleAdd}
          disabled={showForm}
          title={showForm ? 'Finish editing first' : undefined}
          style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
        >
          Add User
        </button>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="styled-input" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {notice && (
        <div style={{ margin: '16px 24px 0', background: '#e3f1e4', border: '1px solid #b7dbb9', color: '#1b5e20', borderRadius: 6, padding: '8px 12px', fontSize: 14 }}>
          {notice}
        </div>
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <UserForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            roles={roles}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '16%' }} />
            <col style={{ width: '19%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Roles</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Login</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Mailbox</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Last Sign-in</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm) handleEdit(item); }}
                style={{ cursor: showForm ? 'default' : 'pointer' }}
                title={showForm ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.name}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.email}</td>
                <td style={{ padding: '6px 8px', fontSize: 13, color: item.roles?.length ? undefined : '#888' }}>
                  {item.roles?.length ? item.roles.map((role) => role.name).join(', ') : 'None'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  <LoginStatusPill status={item.login_status} />
                </td>
                <td style={{ padding: '6px 8px', fontSize: 13 }}>
                  <MailboxCell mailbox={item.mailbox} />
                </td>
                <td style={{ padding: '6px 8px', fontSize: 13, color: item.last_signed_in_at ? undefined : '#888' }}>
                  {item.last_signed_in_at ? formatDateTime(item.last_signed_in_at) : 'Never'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  <ActiveStatusPill active={item.is_active} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      ...(item.is_active && item.login_status !== 'active' && item.login_status !== 'paused'
                        ? [{ key: 'invite', label: item.login_status === 'no_login' ? 'Invite' : 'Re-send invite', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleInvite(item) }]
                        : []),
                      ...(item.mailbox
                        ? [{ key: 'disconnect-mailbox', label: 'Disconnect mailbox', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDisconnectMailbox(item) }]
                        : []),
                      item.is_active
                        ? {
                          key: 'deactivate',
                          label: 'Switch off login',
                          danger: true,
                          disabled: showForm || item.id === currentUser?.id,
                          disabledTitle: showForm ? 'Finish editing first' : "You can't switch off your own login",
                          onClick: () => handleDeactivate(item),
                        }
                        : { key: 'reactivate', label: 'Switch on login', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Users"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default UserTable;
